import {
  ComponentProps,
  ReactNode,
  useEffect,
  useRef,
  useState,
  Key,
} from 'react';
import { UseOpQueryOptions, useOpQuery } from 'utils/customHooks/useOpQuery';
import difference from 'lodash/difference';
import debounce from 'lodash/debounce';
import differenceBy from 'lodash/differenceBy';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import uniqBy from 'lodash/uniqBy';
import { ensurePayloadAndQuery } from 'utils/ensurePayloadAndQuery';
import { OpTag } from '../OpTag/OpTag';
import { OpTransfer } from '../OpTransfer/OpTransfer';
import { OpTooltip } from '../OpTooltip/OpTooltip';
import { OpCollapse } from '../OpCollapse/OpCollapse';

import './OpDataFetchTransfer.scss';

interface DataParams<T extends keyof Api.ClientSpec> {
  queryOptions: UseOpQueryOptions<T>;
  createKey?: (item: Record<string, any>) => string;
  createTitle?: (item: Record<string, any>) => string;
  createDescription?: (item: Record<string, any>) => string;
  createDisabled?: (item: Record<string, any>) => boolean;
  createTooltip?: (item: Record<string, any>) => string | null;
}

export type ExistingItemIdsEndpoint =
  | 'listGroupUserIds'
  | 'listRoleUserIds'
  | 'listSiteUserIds'
  | 'listZoneUserIds'
  | 'listZoneGroupIds';

interface ExistingItemIdsParams<K extends ExistingItemIdsEndpoint> {
  queryOptions: UseOpQueryOptions<K, number[]>;
  createDisabled?: (item: Record<string, any>) => boolean;
}

interface RecordType {
  key?: Key;
  title?: string;
  description?: string;
  disabled?: boolean;
  tooltip?: string | null;
}

interface Queries {
  limit?: number;
  offset?: number;
  sort?: string;
  order?: string;
  q?: string;
  filter?: string;
  preFilter?: string;
  options?: string;
}

interface OpDataFetchTransferProps<
  T extends keyof Api.ClientSpec,
  U extends keyof Api.ClientSpec,
  K extends ExistingItemIdsEndpoint,
> extends Omit<ComponentProps<typeof OpTransfer>, 'onChange'> {
  availableItemsParams: DataParams<T>;
  existingItemsParams: DataParams<U>;
  existingItemIdsParams: ExistingItemIdsParams<K>;
  createLabel: (record: RecordType) => ReactNode;
  existingItemsLabel?: string;
  testId?: string;
  onChange?: (idsToAddAndRemove: { add: number[]; remove: number[] }) => void;
  customRender?: (
    record: RecordType,
    targetKeys: Key[],
    addedItems: RecordType[],
    removedItems: RecordType[],
  ) => any;
  collapsible?: false | { label: string; isOpen?: boolean };
}

const appendQueriesInQueryOptions = <T extends keyof Api.ClientSpec>(
  queryOptions: UseOpQueryOptions<T>,
  queriesToAppend: Queries,
) => {
  const { parametersArray, withQuery, withPayload } = ensurePayloadAndQuery(
    queryOptions.apiEndpointName,
    queryOptions.parameters,
  );
  if (
    withQuery ||
    (queryOptions.apiEndpointName.endsWith('Post') && withPayload) // For POST versions of GET requests, we need to ensure the payload is included (e.g. listUsersPost). See `autoGeneratePostList` in Helium for more info.
  ) {
    // Find the index of the queries (last object)
    const queriesIndex = parametersArray.findLastIndex(
      (param: any) => typeof param === 'object',
    );

    if (queriesIndex === -1) {
      // ensurePayloadAndQuery guarantees the presence of a queries object, so throw since something is way off...
      throw new Error(
        `Critical: ensurePayloadAndQuery did not ensure a query object for API endpoint ${queryOptions.apiEndpointName}`,
      );
    }

    parametersArray[queriesIndex] = {
      ...(parametersArray[queriesIndex] as Queries),
      ...queriesToAppend,
    } satisfies Queries;
  }

  return {
    ...queryOptions,
    parameters: parametersArray,
  } as UseOpQueryOptions<T>;
};

export const OpDataFetchTransfer = <
  T extends keyof Api.ClientSpec,
  U extends keyof Api.ClientSpec,
  K extends ExistingItemIdsEndpoint,
>({
  className = '',
  testId = 'op-data-fetch-transfer',
  availableItemsParams,
  existingItemsParams,
  existingItemIdsParams,
  createLabel = (record: RecordType) => record.title,
  existingItemsLabel,
  onChange: onChangeProp,
  customRender,
  collapsible = false,
  // Props passed through to OpTransfer
  ...opTransferProps
}: OpDataFetchTransferProps<T, U, K>) => {
  const { t } = useTranslation();

  const [availableDataSource, setAvailableDataSource] = useState<RecordType[]>(
    [],
  );
  const [existingDataSource, setExistingDataSource] = useState<RecordType[]>(
    [],
  );
  const [availableTotalItems, setAvailableTotalItems] = useState<number>();
  const [existingTotalItems, setExistingTotalItems] = useState<number>();
  const [availableItemsQuery, setAvailableItemsQuery] = useState<string>();
  const [existingItemsQuery, setExistingItemsQuery] = useState<string>();
  const [targetKeys, setTargetKeys] = useState<Key[]>([]);
  const addedItems = useRef<RecordType[]>([]);
  const removedItems = useRef<RecordType[]>([]);

  // Get the existing items ids so we can exclude them from the available items
  const { data: existingItemsIds } = useOpQuery({
    ...existingItemIdsParams.queryOptions,
    select: (heliumResponse) => heliumResponse?.json?.data,
  });

  // Find the last object in the parameters array
  const parameterQueryOptionsPreFilter =
    (
      availableItemsParams.queryOptions?.parameters?.findLast(
        (param) => typeof param === 'object',
      ) as Queries
    )?.preFilter || '';

  const availableItemsIdsToFilterOut = (existingItemsIds || []).concat(
    addedItems.current.map(({ key }) => Number(key)),
  );

  // We need to exclude the existing items from the available items
  const availableItemsPreFilter =
    parameterQueryOptionsPreFilter +
    (availableItemsIdsToFilterOut.length
      ? ` id:[${availableItemsIdsToFilterOut.map((id) => `!=${Number(id)}`).join(',')}]`
      : '');
  const { data: availableItemsHeliumResponse } = useOpQuery(
    appendQueriesInQueryOptions<T>(availableItemsParams.queryOptions, {
      limit: 100,
      ...(availableItemsPreFilter && {
        preFilter: availableItemsPreFilter.trim(),
      }),
      ...(availableItemsQuery && { q: availableItemsQuery }),
    }),
  );

  const existingItemIdsToFilterOut = removedItems.current.map(({ key }) =>
    Number(key),
  );

  const { data: existingItemsHeliumResponse } = useOpQuery(
    appendQueriesInQueryOptions<U>(existingItemsParams.queryOptions, {
      limit: 100,
      ...(existingItemIdsToFilterOut.length && {
        preFilter: `id:[${existingItemIdsToFilterOut.map((id) => `!=${Number(id)}`).join(',')}]`,
      }),
      ...(existingItemsQuery && { q: existingItemsQuery }),
    }),
  );

  // Cleanup
  useEffect(
    () => () => {
      addedItems.current = [];
      removedItems.current = [];
    },
    [],
  );

  // Set the existingDataSource
  useEffect(() => {
    const existingItemsData = existingItemsHeliumResponse?.json?.data || [];
    // Add back removed items, marking them as removed
    const removed = removedItems.current.map((item) => ({
      ...item,
      removed: true, // add a flag for rendering
    }));

    if (Array.isArray(existingItemsData)) {
      // Merge, ensuring no duplicates (removed items not in fetched data)
      const merged = uniqBy(
        [
          ...existingItemsData.map((item) => ({
            key: existingItemsParams.createKey?.(item) || String(item.id),
            title: existingItemsParams.createTitle?.(item) || String(item.name),
            description: existingItemsParams.createDescription?.(item),
            tooltip: existingItemsParams.createTooltip?.(item),
            disabled: existingItemsParams.createDisabled?.(item),
            removed: false,
          })),
          ...removed.filter(
            (removedItem) =>
              !existingItemsData.some(
                (item) =>
                  (existingItemsParams.createKey?.(item) || String(item.id)) ===
                  removedItem.key,
              ),
          ),
        ],
        'key',
      );

      setExistingTotalItems(existingItemsHeliumResponse?.json?.totalCount);

      setExistingDataSource(merged);

      setTargetKeys(
        merged.filter((item) => !item.removed).map((item) => item.key || ''),
      );
    }
  }, [
    existingItemsHeliumResponse?.json?.data,
    existingItemsHeliumResponse?.json?.totalCount,
    existingItemsParams,
  ]);

  // Set the availableDataSource
  useEffect(() => {
    const availableItemsData = availableItemsHeliumResponse?.json?.data;

    if (Array.isArray(availableItemsData)) {
      setAvailableTotalItems(
        availableItemsHeliumResponse?.json?.totalCount || 0,
      );

      setAvailableDataSource(() =>
        uniqBy(
          availableItemsData.map((item) => ({
            key: availableItemsParams.createKey?.(item) || String(item.id),
            title:
              availableItemsParams.createTitle?.(item) || String(item.name),
            description: availableItemsParams.createDescription?.(item),
            disabled: availableItemsParams.createDisabled?.(item),
            tooltip: availableItemsParams.createTooltip?.(item),
          })),
          'key',
        ),
      );
    }
  }, [
    availableItemsHeliumResponse?.json?.data,
    availableItemsHeliumResponse?.json?.totalCount,
    availableItemsParams,
  ]);

  const onChange: ComponentProps<typeof OpTransfer>['onChange'] = (
    newTargetKeys,
    direction,
    moveKeys,
  ) => {
    // Update targetKeys
    setTargetKeys(newTargetKeys);

    if (direction === 'right') {
      // Only add tag if wasn't there originally
      const moveKeysNotInRemoveKeys = difference(
        moveKeys,
        removedItems.current.map(({ key }) => key),
      );

      const newAddedItems = [
        ...addedItems.current,
        ...dataSource.filter(({ key }) =>
          moveKeysNotInRemoveKeys.includes(key),
        ),
      ];
      const newRemovedItems = differenceBy(
        removedItems.current,
        dataSource.filter(({ key }) => key && moveKeys.includes(key)),
        'key',
      );

      onChangeProp?.({
        add: newAddedItems.map(({ key }) => Number(key)),
        remove: newRemovedItems.map(({ key }) => Number(key)),
      });

      addedItems.current = newAddedItems;

      // Remove old tags
      removedItems.current = newRemovedItems;
    } else {
      // Only add tag if wasn't there originally
      const moveKeysNotInAddKeys = difference(
        moveKeys,
        addedItems.current.map(({ key }) => key),
      );

      const newRemovedItems = [
        ...removedItems.current,
        ...dataSource.filter(({ key }) => moveKeysNotInAddKeys.includes(key)),
      ];
      const newAddedItems = differenceBy(
        addedItems.current,
        dataSource.filter(({ key }) => key && moveKeys.includes(key)),
        'key',
      );

      onChangeProp?.({
        remove: newRemovedItems.map(({ key }) => Number(key)),
        add: newAddedItems.map(({ key }) => Number(key)),
      });

      removedItems.current = newRemovedItems;

      // Remove old tags
      addedItems.current = newAddedItems;
    }
  };

  // Stable debounced search handler
  const debouncedSearchRef = useRef(
    debounce((direction, value) => {
      if (direction === 'left') {
        setAvailableItemsQuery(value);
      } else {
        setExistingItemsQuery(value);
      }
    }, 300),
  );

  // For OpTransfer, use debouncedSearchRef.current
  const onSearch = debouncedSearchRef.current;

  // DataSource is the combined responses from available and existing item endpoints
  let dataSource = availableDataSource.concat(existingDataSource);

  // If there is an existing items query, add removed items to the end of the dataSource
  if (existingItemsQuery) {
    dataSource.push(...removedItems.current);
  }

  // Ensure added items persist in the dataSource
  if (availableItemsQuery) {
    dataSource.push(...addedItems.current);
  } else {
    // Add added items to the dataSource if not already present
    const addedItemsNotInDataSource = addedItems.current.filter(
      ({ key }) => !dataSource.some((item) => item.key === key),
    );
    dataSource.push(...addedItemsNotInDataSource);
  }

  // Make sure dataSource items are unique
  dataSource = uniqBy(dataSource, 'key');

  // Sort the dataSource so removed items are at top of available list
  dataSource = dataSource.sort((a, b) => {
    // Sorted so removed items are at top of available list
    const aKeyIndex = a.key
      ? removedItems.current.map(({ key }) => key).indexOf(a.key)
      : -1;
    const bKeyIndex = b.key
      ? removedItems.current.map(({ key }) => key).indexOf(b.key)
      : -1;

    if (aKeyIndex !== -1 && bKeyIndex === -1) {
      // a's key is in removedItems, but b's key is not
      return -1;
    }
    if (aKeyIndex === -1 && bKeyIndex !== -1) {
      // b's key is in removedItems, but a's key is not
      return 1;
    }
    // Both a's key and b's key are in removedItems or both are not in removedItems
    // Preserve the original order
    return 0;
  });

  // Ensure targetKeys includes all added items
  const uniqueTargetKeys = [
    ...new Set(addedItems.current.map(({ key }) => key!).concat(targetKeys)),
  ];

  const component = (
    <OpTransfer
      className={clsx('op-data-fetch-transfer', className)}
      dataSource={dataSource}
      showSearch
      testId={testId}
      selectAllLabels={[
        ({
          selectedCount,
          totalCount,
        }: {
          selectedCount: number;
          totalCount: number;
        }) => {
          const count =
            selectedCount > 0
              ? t('{{count}}/{{totalCount}} items selected', {
                  count: selectedCount,
                  totalCount,
                })
              : t('Select {{count}} item', { count: totalCount });

          const displayedAvailableTotalItems =
            (availableTotalItems || 0) + removedItems.current.length;

          const total =
            displayedAvailableTotalItems &&
            totalCount !== displayedAvailableTotalItems
              ? `(${t('{{availableTotalItems}} total', {
                  availableTotalItems: displayedAvailableTotalItems,
                })})`
              : '';

          return `${count} ${total}`;
        },
        ({
          selectedCount,
          totalCount,
        }: {
          selectedCount: number;
          totalCount: number;
        }) => {
          const count =
            selectedCount > 0
              ? t('{{count}}/{{totalCount}} items selected', {
                  count: selectedCount,
                  totalCount,
                })
              : t('{{prefix}}{{count}} item', {
                  count: totalCount,
                  prefix: opTransferProps.oneWay ? '' : t('Select '),
                });

          const displayedExistingTotalItems =
            (existingTotalItems || 0) + addedItems.current.length;

          const total =
            displayedExistingTotalItems &&
            totalCount !== displayedExistingTotalItems
              ? `(${t('{{existingTotalItems}} total', {
                  existingTotalItems: displayedExistingTotalItems,
                  existingItemsLabel: existingItemsLabel || t('total'),
                })})`
              : '';

          return `${count} ${total}`;
        },
      ]}
      listStyle={{
        width: '100%',
        height: 500,
        overflow: 'hidden', // Keeps overall width constrained to drawer width
      }}
      targetKeys={uniqueTargetKeys}
      onChange={onChange}
      onSearch={onSearch}
      filterOption={(inputValue, option) => {
        return option.title!.toLowerCase().includes(inputValue.toLowerCase());
      }}
      render={(record: RecordType) => {
        const { key, title, tooltip } = record;

        const isAdded = addedItems.current
          .map(({ key: addedItemKey }) => addedItemKey)
          .includes(key);
        const isRemoved = removedItems.current
          .map(({ key: removedItemKey }) => removedItemKey)
          .includes(key);

        return customRender
          ? customRender(
              record,
              uniqueTargetKeys,
              addedItems.current,
              removedItems.current,
            )
          : {
              label: (
                <OpTooltip title={tooltip} placement="topLeft">
                  <div className="op-data-fetch-transfer__label-wrapper">
                    <div className="op-data-fetch-transfer__label">
                      {createLabel(record)}
                    </div>
                    {isAdded && (
                      <OpTooltip title={t('Will add on save')}>
                        <OpTag className="op-data-fetch-transfer__tag">
                          {t('adding')}
                        </OpTag>
                      </OpTooltip>
                    )}
                    {isRemoved && (
                      <OpTooltip title={t('Will remove on save')}>
                        <OpTag className="op-data-fetch-transfer__tag">
                          {t('removing')}
                        </OpTag>
                      </OpTooltip>
                    )}
                  </div>
                </OpTooltip>
              ),
              value: title || '--',
            };
      }}
      {...opTransferProps}
    />
  );

  // If the `collapsible` prop is provided, wrap the component in a collapsible UI element.
  // The `collapsible.label` is used as the label for the collapsible header, and the total
  // number of items (including added and removed items) is displayed in parentheses.
  return collapsible ? (
    <OpCollapse
      items={[
        {
          key: '1',
          label: `${collapsible.label} (${
            (existingTotalItems || 0) +
            addedItems.current.length -
            removedItems.current.length
          })`,
          children: component,
        },
      ]}
      {...(collapsible.isOpen && { defaultActiveKey: ['1'] })}
    />
  ) : (
    component
  );
};
